import { ReactNode, useEffect, useMemo, useState } from 'react';

import { Box } from 'components/Box';
import { Layout } from 'layouts/Layout/Layout';
import { CmsApiPage } from 'modules/cms/api/types/CmsApiPage';
import { CmsRecommendedJobsBlock } from 'modules/cms/blocks/RecommendedJobs/CmsRecommendedJobsBlock';
import {
  CmsBodyContainer,
  CmsBodyContainerWidth,
} from 'modules/cms/components/Body/CmsBodyContainer';
import { CmsBodyMainContentPostBelowBlocks } from 'modules/cms/components/Body/CmsBodyMainContentPostBelowBlocks';
import { CmsButtonBar } from 'modules/cms/components/ButtonBar/CmsButtonBar';
import { CmsSocialSidebar } from 'modules/cms/components/SocialSidebar/CmsSocialSidebar';
import { SubscribedToSubsiteModal } from 'modules/cms/components/SubscribedToSubsiteModal/SubscribedToSubsiteModal';
import { getContentLocaleUrl, getHideFooterDivider } from 'modules/cms/helpers';
import {
  useCmsApiPageDefaultSearchType,
  useCmsSubscribeToSubsite,
} from 'modules/cms/hooks';
import { sectionPadding } from 'theme/theme';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { isDefined } from 'utils/functional';

type Props = {
  content: CmsApiPage;
  children: ReactNode;
};

export function CmsLayout({ content, children }: Props) {
  const [showSubscribedModal, setShowSubscribedModal] = useState(false);

  const { subscribe, isSubscribed } = useCmsSubscribeToSubsite(
    content.subsite.id,
  );
  const defaultSearchType = useCmsApiPageDefaultSearchType({
    cmsApiPage: content,
  });

  const hero = content.heroSearch?.onlyResults ? undefined : content.heroSearch;

  const hasBelowBlocks = useMemo(
    () =>
      content.hasRecommendedJobs ||
      (content.tags && content.tags.length > 0) ||
      content.author ||
      content.hasReactions,
    [
      content.author,
      content.hasReactions,
      content.hasRecommendedJobs,
      content.tags,
    ],
  );

  const hideFooterDivider = getHideFooterDivider({ content });
  const localeUrl = getContentLocaleUrl({ content });
  const showButtonBar =
    content.type === 'POST' &&
    (content.subsite.canSubscribe || content.hasSocialSharing);

  useEffect(() => {
    if (isSubscribed) {
      setShowSubscribedModal(true);
      trackClicked('Subscribe', {
        context: 'CMS Page',
      });
    }
  }, [isSubscribed, setShowSubscribedModal]);

  return (
    <>
      <Layout
        buttonBar={
          showButtonBar ? (
            <CmsButtonBar
              hasSocialShare={content.hasSocialSharing}
              subscribe={subscribe}
              canSubscribe={content.subsite.canSubscribe}
              isSubscribed={isSubscribed}
              shareUrl={getContentLocaleUrl({ content })}
            />
          ) : undefined
        }
        content={content}
        hero={hero}
        stickySidebar={
          content.type === 'POST' ? (
            <CmsSocialSidebar
              hasRecentJobs={content.hasRecommendedJobs}
              hasSocialShare={content.hasSocialSharing}
              subscribe={subscribe}
              canSubscribe={content.subsite.canSubscribe}
              isSubscribed={isSubscribed}
              subsiteSubscribeText={content.subsite.subscribeText}
              shareUrl={getContentLocaleUrl({ content })}
            />
          ) : null
        }
        defaultWithSearchHero={isDefined(content.heroSearch)}
        showListingCounts={
          isDefined(content.heroSearch) && content.heroSearch.showCounts
        }
        navSection="MAIN"
        searchMode={false}
        defaultSearchType={defaultSearchType}
        hideFooterDivider={hideFooterDivider}
        urlByLocale={localeUrl}
        preFooter={
          hasBelowBlocks && (
            <Box
              flex="1 1 auto"
              width="100%"
              m={sectionPadding}
              mt={[16, null, 24]}
            >
              {content.type === 'POST' && (
                <CmsBodyMainContentPostBelowBlocks content={content} />
              )}

              {content.hasRecommendedJobs && (
                <CmsBodyContainer
                  $width={CmsBodyContainerWidth.NARROW_LAYOUT_WIDE}
                >
                  <CmsRecommendedJobsBlock
                    data={{
                      headline: content.recommendedJobsHeadline || null,
                      layout: 'REGULAR',
                      jobFunction: null,
                      areaOfFocus: null,
                    }}
                  />
                </CmsBodyContainer>
              )}
            </Box>
          )
        }
      >
        {children}
      </Layout>
      {showSubscribedModal ? (
        <SubscribedToSubsiteModal
          onClose={() => setShowSubscribedModal(false)}
        />
      ) : null}
    </>
  );
}
